import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import StatusStepper from "@/components/StatusStepper";
import { api } from "@/lib/api";
import { StarRating, StarPicker } from "@/components/StarRating";
import LineItemDetailDialog from "@/components/LineItemDetailDialog";
import { LoyaltyCard } from "@/components/LoyaltyCard";
import { TrackerDepositCard } from "@/components/TrackerDepositCard";
import {
  Search, Loader2, AlertCircle, Zap, MessageCircle, ShieldCheck,
  CheckCircle2, ChevronRight, ArrowLeft, X, CreditCard, Banknote, Copy, Star, Send,
  Upload, Image as ImageIcon, FileCheck2, Truck, ExternalLink,
  ClipboardCheck, Download, ChevronDown, ChevronUp, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

const fileUrl = (path) => `${process.env.REACT_APP_BACKEND_URL}/api/files/${path}`;

const statusBadgeClasses = (status) => {
  switch (status) {
    case "Completed":
      return "bg-emerald-100 text-emerald-800 border border-emerald-200";
    case "Ready for Pickup":
      return "bg-spark-yellow text-spark-ink";
    case "In Progress":
      return "bg-blue-100 text-blue-800 border border-blue-200";
    case "Received":
      return "bg-violet-100 text-violet-800 border border-violet-200";
    case "Awaiting Quote Approval":
      return "bg-pink-100 text-pink-800 border border-pink-200";
    case "Long-Term Project":
      return "bg-indigo-100 text-indigo-800 border border-indigo-200";
    case "Waiting on Parts":
      return "bg-orange-100 text-orange-800 border border-orange-200";
    case "Awaiting Drop-off":
      return "bg-slate-100 text-slate-700 border border-slate-200";
    case "Cancelled":
      return "bg-slate-200 text-slate-600 border border-slate-300 line-through";
    default:
      return "bg-spark-teal-deep text-white";
  }
};

const REVIEWED_KEY = "spark_reviewed_jobs";
const wasReviewed = (ref) => {
  try {
    const arr = JSON.parse(localStorage.getItem(REVIEWED_KEY) || "[]");
    return arr.includes(ref);
  } catch { return false; }
};
const markReviewed = (ref) => {
  try {
    const arr = JSON.parse(localStorage.getItem(REVIEWED_KEY) || "[]");
    if (!arr.includes(ref)) arr.push(ref);
    localStorage.setItem(REVIEWED_KEY, JSON.stringify(arr));
  } catch { /* ignore */ }
};

const OUTCOME_STYLES = {
  "Passed": { pill: "bg-emerald-100 text-emerald-800 border-emerald-200", accent: "bg-emerald-500", copy: "All checks passed" },
  "Passed with concerns": { pill: "bg-amber-100 text-amber-800 border-amber-200", accent: "bg-amber-500", copy: "Passed — with a couple of things to keep an eye on" },
  "Failed": { pill: "bg-red-100 text-red-800 border-red-200", accent: "bg-red-500", copy: "Some items need follow-up" },
};

const ChecklistSummaryCard = ({ job, customerEmail }) => {
  const [expanded, setExpanded] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const s = job.checklist_summary;
  if (!s) return null;
  const style = OUTCOME_STYLES[s.outcome] || OUTCOME_STYLES["Passed"];
  const pct = s.total ? Math.round((s.done / s.total) * 100) : 0;
  const completedStr = s.completed_at
    ? new Date(s.completed_at).toLocaleString("en-NZ", { dateStyle: "medium", timeStyle: "short" })
    : "";

  const downloadReport = async () => {
    setDownloading(true);
    try {
      const em = customerEmail || (new URLSearchParams(window.location.search)).get("email") || "";
      const res = await api.get("/track/checklist.pdf", {
        params: { email: em, reference: job.ref_number },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Spark-Service-Report-${job.ref_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { a.remove(); URL.revokeObjectURL(url); }, 1000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't download the report");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="surface-card overflow-hidden" data-testid="tracker-checklist">
      <div className={`${style.accent} h-1.5 w-full`} />
      <div className="p-5 sm:p-8">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
          <div className="min-w-0">
            <p className="label-mono text-spark-teal flex items-center gap-1.5">
              <ClipboardCheck className="size-3.5" /> Service Report
            </p>
            <h3 className="font-display uppercase tracking-tight text-lg sm:text-xl font-bold text-spark-ink mt-1">
              {s.template_name}
            </h3>
            <p className="text-xs text-spark-slate mt-0.5">{style.copy}{completedStr && ` · ${completedStr}`}</p>
          </div>
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm border font-display uppercase tracking-wider text-xs font-bold ${style.pill}`} data-testid="tracker-checklist-outcome">
            <CheckCircle2 className="size-3.5" /> {s.outcome}
          </span>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="rounded-md bg-spark-floor border border-spark-line px-3 py-2.5 text-center">
            <p className="text-[10px] uppercase tracking-wider text-spark-slate font-bold">Items done</p>
            <p className="font-display text-2xl font-bold text-spark-ink leading-tight mt-0.5">{s.done}<span className="text-sm text-spark-slate font-normal">/{s.total}</span></p>
          </div>
          <div className="rounded-md bg-spark-floor border border-spark-line px-3 py-2.5 text-center">
            <p className="text-[10px] uppercase tracking-wider text-spark-slate font-bold">Marked N/A</p>
            <p className="font-display text-2xl font-bold text-spark-ink leading-tight mt-0.5">{s.skipped}</p>
          </div>
          <div className="rounded-md bg-spark-teal-deep text-white px-3 py-2.5 text-center">
            <p className="text-[10px] uppercase tracking-wider text-spark-yellow font-bold">Progress</p>
            <p className="font-display text-2xl font-bold leading-tight mt-0.5">{pct}%</p>
          </div>
        </div>

        {s.technician_notes && (
          <div className="rounded-md bg-spark-yellow/20 border-l-4 border-spark-yellow px-4 py-3 mb-4" data-testid="tracker-checklist-notes">
            <p className="label-mono text-spark-teal-dark mb-1 flex items-center gap-1.5">
              <AlertTriangle className="size-3" /> Notes from your technician
            </p>
            <p className="text-sm text-spark-ink whitespace-pre-wrap leading-relaxed">{s.technician_notes}</p>
          </div>
        )}

        <div className="flex items-center justify-between flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="inline-flex items-center gap-1.5 text-sm text-spark-teal-dark hover:text-spark-teal font-semibold"
            data-testid="tracker-checklist-expand"
          >
            {expanded ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
            {expanded ? "Hide item list" : "Show every item that was checked"}
          </button>
          {s.has_pdf && (
            <button
              type="button"
              onClick={downloadReport}
              disabled={downloading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm bg-spark-teal text-white text-xs uppercase tracking-wider font-bold hover:bg-spark-teal-dark disabled:opacity-60"
              data-testid="tracker-checklist-download"
            >
              {downloading ? <Loader2 className="size-3.5 animate-spin" /> : <Download className="size-3.5" />}
              Download service report
            </button>
          )}
        </div>

        {expanded && (
          <div className="mt-4 pt-4 border-t border-spark-line space-y-3" data-testid="tracker-checklist-items">
            {(s.sections || []).map((sec, si) => {
              const secDone = (sec.items || []).filter((i) => i.checked).length;
              const secTotal = (sec.items || []).length;
              return (
                <div key={si} className="rounded-md border border-spark-line overflow-hidden">
                  <div className="flex items-center justify-between gap-3 px-3 py-2 bg-spark-floor">
                    <p className="font-display uppercase tracking-tight font-bold text-spark-ink text-sm truncate">{sec.title}</p>
                    <span className="text-[10px] uppercase tracking-wider font-bold text-spark-slate">{secDone}/{secTotal}</span>
                  </div>
                  <ul className="divide-y divide-spark-line">
                    {(sec.items || []).map((it, ii) => (
                      <li key={ii} className="flex items-start gap-2 px-3 py-1.5 text-sm">
                        <span
                          className={`mt-0.5 shrink-0 size-4 rounded-sm border flex items-center justify-center ${
                            it.checked ? "bg-emerald-500 border-emerald-500 text-white" : it.skipped ? "bg-amber-100 border-amber-300" : "border-spark-line"
                          }`}
                          aria-hidden
                        >
                          {it.checked && <CheckCircle2 className="size-2.5" strokeWidth={3} />}
                        </span>
                        <span className={`flex-1 ${it.checked ? "text-spark-slate" : "text-spark-ink"}`}>{it.label}</span>
                        {it.skipped && <span className="text-[10px] uppercase tracking-wider font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded shrink-0">N/A</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const ReviewPromptCard = ({ job }) => {
  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState(() => wasReviewed(job.ref_number));
  const [rating, setRating] = useState(5);
  const [body, setBody] = useState("");
  const [title, setTitle] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (submitted) {
    return (
      <div className="surface-card p-5 border-l-4 border-l-emerald-500 bg-emerald-50 flex items-center gap-3" data-testid="review-thanks-card">
        <CheckCircle2 className="size-6 text-emerald-600 shrink-0" />
        <div>
          <p className="font-display uppercase tracking-wider font-bold text-spark-ink">Thanks for the review!</p>
          <p className="text-xs text-spark-slate mt-0.5">It'll appear on our site once approved.</p>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (body.trim().length < 10) {
      toast.error("Tell us a bit more — at least 10 characters");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/reviews", {
        customer_name: job.full_name,
        rating,
        title: title.trim() || null,
        body: body.trim(),
        job_ref: job.ref_number,
      });
      markReviewed(job.ref_number);
      setSubmitted(true);
      toast.success("Thanks! Your review will appear once approved.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="surface-card overflow-hidden border-l-4 border-l-spark-yellow" data-testid="review-prompt-card">
      <div className="p-5 sm:p-6 flex items-start gap-4">
        <div className="size-12 rounded-full bg-spark-yellow flex items-center justify-center shrink-0">
          <Star className="size-6 fill-spark-ink text-spark-ink" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="label-mono text-spark-teal">Loved your experience?</p>
          <p className="font-display uppercase tracking-tight text-xl font-bold text-spark-ink mt-0.5 leading-tight">
            Help us out — drop a quick review
          </p>
          <p className="text-sm text-spark-slate mt-1.5">
            Takes 30 seconds. Helps fellow scooter riders find us — and means a lot to a small workshop like ours.
          </p>
          {!open && (
            <div className="flex flex-wrap gap-2 mt-4">
              <button onClick={() => setOpen(true)} className="btn-accent !py-2.5 !px-4 text-xs" data-testid="open-review-form">
                <Star className="size-4 fill-spark-ink" /> Leave a review
              </button>
              <Link to="/reviews" className="btn-outline-ink !py-2.5 !px-4 text-xs">See others</Link>
            </div>
          )}
        </div>
      </div>
      {open && (
        <form onSubmit={submit} className="bg-spark-floor border-t border-spark-line p-5 sm:p-6 space-y-4" data-testid="review-prompt-form">
          <div>
            <label className="label-mono mb-2 inline-block">Your rating</label>
            <div><StarPicker value={rating} onChange={setRating} /></div>
          </div>
          <div>
            <label className="label-mono mb-2 inline-block">Headline (optional)</label>
            <input className="input-style" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Saved my commute" />
          </div>
          <div>
            <label className="label-mono mb-2 inline-block">Your review</label>
            <textarea className="input-style resize-none" rows={4} required minLength={10} maxLength={2000} value={body} onChange={(e) => setBody(e.target.value)} placeholder="What did you bring in, how did it go, how's the scooter now?" data-testid="review-prompt-body" />
            <p className="text-[10px] text-spark-slate mt-1 text-right">{body.length}/2000</p>
          </div>
          <p className="text-xs text-spark-slate">Posting as <strong>{job.full_name}</strong> · Job <strong>{job.ref_number}</strong></p>
          <div className="flex flex-wrap gap-2">
            <button type="submit" disabled={submitting} className="btn-accent" data-testid="submit-review-prompt">
              {submitting ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />} Post my review
            </button>
            <button type="button" onClick={() => setOpen(false)} className="btn-outline-ink">Maybe later</button>
          </div>
        </form>
      )}
    </div>
  );
};

const PaymentStatusBlock = ({ job, customerEmail, onUploaded }) => {
  const [settings, setSettings] = useState(null);
  useEffect(() => {
    api.get("/settings/public").then((res) => setSettings(res.data)).catch(() => {});
  }, []);
  const status = (job.payment_status || "Unpaid").toLowerCase();
  const isPaid = status === "paid";
  const isPartial = status === "partial";
  // Show the amount the customer should pay. Prefer final_price (admin-locked) when
  // present, otherwise fall back to the approved-quote total so customers can pay
  // up-front the moment they approve a quote (before work is done).
  const total = Number(job.final_price ?? job.quoted_total ?? 0);
  const paidSoFar = Number(job.amount_paid || 0);
  const balance = Math.max(0, total - paidSoFar);
  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied");
  };
  const [bank, setBank] = useState(null);
  useEffect(() => {
    api.get("/settings/payment").then((res) => setBank(res.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="tracker-payment" className="space-y-3">
      {isPaid && job.status === "Completed" && (
        <ReviewPromptCard job={job} />
      )}
      <div className={`rounded-md p-5 sm:p-6 ${isPaid ? "bg-emerald-500 text-white" : isPartial ? "bg-spark-yellow text-spark-ink" : "bg-spark-yellow text-spark-ink"}`} data-testid="tracker-payment-card">
        <div className="flex items-start gap-4">
          <div className={`size-12 rounded-full flex items-center justify-center shrink-0 ${isPaid ? "bg-white/20" : "bg-spark-ink/10"}`}>
            {isPaid ? <CheckCircle2 className="size-6" /> : <CreditCard className="size-6" />}
          </div>
          <div className="flex-1 min-w-0">
            {isPaid ? (
              <>
                <p className="label-mono">Paid in full · thank you</p>
                <p className="font-display uppercase tracking-tight text-3xl font-bold mt-0.5 break-words" data-testid="tracker-final-price">${total.toFixed(2)}</p>
              </>
            ) : isPartial ? (
              <>
                <p className="label-mono">Balance owing</p>
                <p className="font-display uppercase tracking-tight text-4xl sm:text-5xl font-bold mt-0.5 break-words" data-testid="tracker-balance">${balance.toFixed(2)}</p>
                <p className="text-xs mt-1.5 opacity-80">
                  of <span className="font-bold">${total.toFixed(2)}</span> total · You've paid <span className="font-bold text-emerald-700">${paidSoFar.toFixed(2)}</span> so far. <span className="block sm:inline">Reference <span className="font-bold">{job.ref_number}</span> when paying the rest.</span>
                </p>
              </>
            ) : (
              <>
                <p className="label-mono">{job.final_price ? "Final price · awaiting payment" : "Approved quote · pay anytime"}</p>
                <p className="font-display uppercase tracking-tight text-3xl font-bold mt-0.5 break-words" data-testid="tracker-final-price">${total.toFixed(2)}</p>
                <p className="text-xs mt-1 opacity-80">Reference <span className="font-bold">{job.ref_number}</span> when paying.</p>
              </>
            )}
          </div>
        </div>
        {isPartial && (
          <div className="mt-4" data-testid="tracker-payment-progress">
            <div className="flex justify-between text-[10px] uppercase tracking-[0.2em] font-bold mb-1.5">
              <span>Paid {total > 0 ? Math.min(100, Math.round((paidSoFar / total) * 100)) : 0}%</span>
              <span>${paidSoFar.toFixed(2)} / ${total.toFixed(2)}</span>
            </div>
            <div className="h-2.5 w-full rounded-full bg-spark-ink/15 overflow-hidden">
              <div
                className="h-full bg-emerald-600 transition-all duration-700"
                style={{ width: `${total > 0 ? Math.min(100, (paidSoFar / total) * 100) : 0}%` }}
              />
            </div>
          </div>
        )}
      </div>
      {!isPaid && bank?.bank_account_number && (
        <div className="surface-card p-5" data-testid="tracker-bank-details">
          <p className="label-mono text-spark-teal flex items-center gap-2"><Banknote className="size-3.5" /> Bank transfer (preferred)</p>
          <ul className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
            {bank.bank_account_name && <li className="flex justify-between sm:block"><span className="text-spark-slate sm:block sm:text-xs sm:uppercase sm:tracking-wider">Account name</span><span className="font-semibold text-spark-ink">{bank.bank_account_name}</span></li>}
            {bank.bank_name && <li className="flex justify-between sm:block"><span className="text-spark-slate sm:block sm:text-xs sm:uppercase sm:tracking-wider">Bank</span><span className="font-semibold text-spark-ink">{bank.bank_name}</span></li>}
            <li className="flex justify-between sm:block sm:col-span-2">
              <span className="text-spark-slate sm:block sm:text-xs sm:uppercase sm:tracking-wider">Account number</span>
              <span className="font-mono font-bold text-spark-ink flex items-center gap-2">
                {bank.bank_account_number}
                <button onClick={() => copy(bank.bank_account_number)} className="text-spark-teal hover:text-spark-teal-deep" aria-label="Copy account number">
                  <Copy className="size-3.5" />
                </button>
              </span>
            </li>
            <li className="flex justify-between sm:block sm:col-span-2">
              <span className="text-spark-slate sm:block sm:text-xs sm:uppercase sm:tracking-wider">Reference</span>
              <span className="font-mono font-bold text-spark-ink flex items-center gap-2">
                {job.ref_number}
                <button onClick={() => copy(job.ref_number)} className="text-spark-teal hover:text-spark-teal-deep" aria-label="Copy reference"><Copy className="size-3.5" /></button>
              </span>
            </li>
          </ul>
          <p className="text-xs text-spark-slate mt-3 italic">Once funds clear we'll mark this paid and email a tax receipt.</p>
        </div>
      )}
      {!isPaid && (
        <PaymentProofUploader job={job} customerEmail={customerEmail} onUploaded={onUploaded} />
      )}
      {!!(job.payment_proofs && job.payment_proofs.length) && (
        <UploadedProofsList proofs={job.payment_proofs} />
      )}
    </div>
  );
};

const PaymentProofUploader = ({ job, customerEmail, onUploaded }) => {
  const [file, setFile] = useState(null);
  const [note, setNote] = useState("");
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(false);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error("File too large (max 10MB)");
      return;
    }
    setFile(f);
  };

  const submit = async () => {
    if (!file) return toast.error("Pick a screenshot first");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("email", customerEmail || job.email || "");
      fd.append("reference", job.ref_number);
      fd.append("note", note.trim());
      await api.post("/track/payment-proof", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(true);
      setFile(null);
      setNote("");
      toast.success("Screenshot received — we'll verify it shortly");
      if (typeof onUploaded === "function") onUploaded();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  if (done) {
    return (
      <div className="surface-card p-5 border-l-4 border-l-emerald-500 bg-emerald-50 flex items-start gap-3" data-testid="proof-thanks">
        <FileCheck2 className="size-6 text-emerald-600 shrink-0 mt-0.5" />
        <div>
          <p className="font-display uppercase tracking-wider text-sm font-bold text-emerald-800">Payment screenshot received</p>
          <p className="text-sm text-emerald-700 mt-1">We'll verify and mark your job paid shortly. You'll get a receipt by email once confirmed.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="surface-card p-5" data-testid="payment-proof-uploader">
      <p className="label-mono text-spark-teal flex items-center gap-2"><Upload className="size-3.5" /> Send proof of payment</p>
      <p className="text-sm text-spark-slate mt-2">Already paid by bank transfer? Upload a screenshot here so we can verify before pickup. Images or PDF · max 10MB.</p>
      <div className="mt-4 space-y-3">
        <label className="flex items-center justify-center gap-2 px-4 py-3 rounded-md border-2 border-dashed border-spark-line hover:border-spark-teal hover:bg-spark-teal/5 cursor-pointer transition" data-testid="payment-proof-pick-btn">
          <ImageIcon className="size-4 text-spark-teal" />
          <span className="text-sm font-semibold text-spark-ink">{file ? file.name : "Choose a screenshot…"}</span>
          <input type="file" accept="image/*,application/pdf" className="hidden" onChange={onPick} disabled={uploading} data-testid="payment-proof-input" />
        </label>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Optional note (e.g. 'paid from joint account')"
          rows={2}
          maxLength={500}
          className="w-full rounded-md border border-spark-line px-3 py-2 text-sm bg-white text-spark-ink focus:outline-none focus:ring-2 focus:ring-spark-teal/40"
          data-testid="payment-proof-note"
          disabled={uploading}
        />
        <button
          onClick={submit}
          disabled={!file || uploading}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-spark-teal text-white text-sm uppercase tracking-wider font-bold hover:bg-spark-teal-deep disabled:opacity-50"
          data-testid="payment-proof-submit"
        >
          {uploading ? <><Loader2 className="size-4 animate-spin" /> Uploading…</> : <><Send className="size-4" /> Send screenshot</>}
        </button>
      </div>
    </div>
  );
};

const UploadedProofsList = ({ proofs }) => (
  <div className="surface-card p-5" data-testid="uploaded-proofs-list">
    <p className="label-mono text-spark-teal flex items-center gap-2"><FileCheck2 className="size-3.5" /> Your uploaded payment screenshots</p>
    <ul className="mt-3 grid sm:grid-cols-3 gap-3">
      {proofs.map((p) => {
        const url = fileUrl(p.storage_path);
        const isImg = (p.content_type || "").startsWith("image/");
        return (
          <li key={p.id} className="relative rounded-md overflow-hidden border border-spark-line group">
            <a href={url} target="_blank" rel="noreferrer" className="block aspect-square">
              {isImg
                ? <img src={url} alt="Payment proof" className="w-full h-full object-cover" />
                : <div className="w-full h-full flex items-center justify-center bg-spark-floor"><FileCheck2 className="size-8 text-spark-teal" /></div>}
            </a>
            {p.verified && (
              <span className="absolute top-1.5 right-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500 text-white text-[10px] uppercase tracking-wider font-bold">
                <CheckCircle2 className="size-3" /> Verified
              </span>
            )}
          </li>
        );
      })}
    </ul>
  </div>
);

const CustomerOrdersList = ({ orders }) => {
  const [openId, setOpenId] = useState(null);
  const fmtDate = (d) => new Date(d).toLocaleDateString("en-NZ", { dateStyle: "medium" });
  const money = (n) => `$${Number(n || 0).toFixed(2)}`;
  return (
    <ul className="space-y-3" data-testid="customer-orders-list">
      <p className="label-mono text-spark-slate">{orders.length} shop order{orders.length === 1 ? "" : "s"} on file</p>
      {orders.map((o) => {
        const isOpen = openId === o.id;
        const isShipped = o.status === "shipped" || o.status === "completed";
        return (
          <li key={o.id} className="surface-card overflow-hidden" data-testid={`customer-order-${o.order_number}`}>
            <button
              onClick={() => setOpenId(isOpen ? null : o.id)}
              className="w-full p-5 sm:p-6 flex items-center gap-4 text-left hover:bg-spark-floor/50 transition"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-display uppercase tracking-wider text-lg font-bold text-spark-ink">{o.order_number}</span>
                  <span className={`px-2.5 py-0.5 rounded-sm font-display uppercase tracking-wider text-[10px] font-bold ${
                    o.status === "completed" ? "bg-emerald-100 text-emerald-800"
                      : o.status === "shipped" ? "bg-blue-100 text-blue-800"
                      : o.status === "paid" ? "bg-yellow-100 text-yellow-900"
                      : o.status === "pending" ? "bg-orange-100 text-orange-800"
                      : o.status === "refunded" || o.status === "cancelled" ? "bg-slate-200 text-slate-700"
                      : "bg-spark-line text-spark-slate"
                  }`}>{o.status === "pending" ? "Awaiting payment" : o.status}</span>
                  <span className="font-display uppercase tracking-tight text-base font-bold text-spark-ink">{money(o.total)}</span>
                </div>
                <p className="text-sm text-spark-slate mt-1.5 truncate">
                  {o.items.length} item{o.items.length === 1 ? "" : "s"} · {o.fulfilment === "shipping" ? "Delivery" : "Workshop pickup"} · {fmtDate(o.created_at)}
                </p>
              </div>
              <ChevronRight className={`size-5 text-spark-slate shrink-0 transition-transform ${isOpen ? "rotate-90" : ""}`} />
            </button>

            {isOpen && (
              <div className="border-t border-spark-line p-5 sm:p-6 bg-spark-floor/30 space-y-4">
                {o.status === "pending" && (
                  <div className="surface-card p-4 border-l-4 border-l-orange-500 bg-orange-50" data-testid={`order-pending-${o.order_number}`}>
                    <p className="font-display uppercase tracking-wider font-bold text-orange-800 text-sm">Payment not completed</p>
                    <p className="text-xs text-orange-700 mt-1">This order was started but Stripe never confirmed the payment. If you closed the checkout window or the card was declined, please head back to the shop and try again. If you're sure you paid, message us and we'll reconcile.</p>
                  </div>
                )}
                {/* Tracking block */}
                {(o.tracking_url || o.tracking_code) && (
                  <div className="surface-card p-4 border-l-4 border-l-spark-teal" data-testid={`order-tracking-${o.order_number}`}>
                    <p className="label-mono text-spark-teal flex items-center gap-2"><Truck className="size-3.5" /> Tracking your parcel</p>
                    <p className="text-sm text-spark-ink mt-2">
                      Tracking number: <span className="font-mono font-bold">{o.tracking_code}</span>
                    </p>
                    {o.tracking_url && (
                      <a href={o.tracking_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 mt-3 px-4 py-2 rounded-md bg-spark-yellow text-spark-ink hover:bg-yellow-400 text-xs uppercase tracking-wider font-bold" data-testid={`open-live-tracking-${o.order_number}`}>
                        <ExternalLink className="size-3.5" /> Open live tracking
                      </a>
                    )}
                    {o.shipped_at && <p className="text-[11px] text-spark-slate mt-2">Shipped {new Date(o.shipped_at).toLocaleDateString("en-NZ", { dateStyle: "medium" })}</p>}
                  </div>
                )}
                {!o.tracking_url && !o.tracking_code && o.fulfilment === "shipping" && isShipped && (
                  <p className="text-xs text-spark-slate italic">Tracking will appear here once your parcel is on the way.</p>
                )}

                {/* Items */}
                <div>
                  <p className="label-mono text-spark-slate mb-2">Items</p>
                  <ul className="divide-y divide-spark-line">
                    {o.items.map((it, i) => (
                      <li key={i} className="py-2 flex items-start justify-between gap-3 text-sm">
                        <div>
                          <p className="text-spark-ink font-semibold">{it.name}</p>
                          <p className="text-[11px] text-spark-slate">qty {it.quantity}</p>
                        </div>
                        <p className="font-display uppercase tabular-nums text-spark-ink">{money(it.price * it.quantity)}</p>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-3 pt-3 border-t border-spark-line space-y-1 text-sm">
                    <div className="flex justify-between text-spark-slate"><span>Subtotal</span><span>{money(o.subtotal)}</span></div>
                    {o.shipping_total > 0 && <div className="flex justify-between text-spark-slate"><span>Shipping</span><span>{money(o.shipping_total)}</span></div>}
                    <div className="flex justify-between text-spark-ink font-display uppercase tracking-wider font-bold pt-2 border-t border-spark-line"><span>Total paid</span><span>{money(o.total)}</span></div>
                    {o.refunded_amount > 0 && <p className="text-xs text-orange-600 text-right mt-1">Refunded: {money(o.refunded_amount)}</p>}
                  </div>
                </div>

                {/* Address */}
                {o.fulfilment === "shipping" && o.shipping_address && (
                  <div>
                    <p className="label-mono text-spark-slate mb-1">Shipping to</p>
                    <p className="text-sm text-spark-ink">{o.shipping_address}</p>
                  </div>
                )}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
};

const TENURE_STYLES = {
  old_timer: { pill: "bg-spark-yellow text-spark-ink border-spark-yellow", banner: "bg-gradient-to-br from-spark-yellow via-yellow-300 to-amber-200 text-spark-ink" },
  cruiser:   { pill: "bg-spark-teal text-white border-spark-teal", banner: "bg-gradient-to-br from-spark-teal to-spark-teal-dark text-white" },
  returner:  { pill: "bg-spark-teal/20 text-spark-teal-dark border-spark-teal/40", banner: "bg-gradient-to-br from-spark-teal/15 to-spark-teal/5 text-spark-teal-dark" },
  new_rider: { pill: "bg-spark-floor text-spark-slate border-spark-line", banner: "bg-spark-floor text-spark-ink" },
};

const TenurePill = ({ profile }) => {
  const tier = profile?.tier || { key: "new_rider", label: "New Rider" };
  const s = TENURE_STYLES[tier.key] || TENURE_STYLES.new_rider;
  return (
    <>
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border text-[10px] uppercase tracking-wider font-bold ${s.pill}`}>
        Since {profile?.since_year || "—"}
      </span>
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border text-[10px] uppercase tracking-wider font-bold ${s.pill}`}>
        <Zap className="size-3" /> {tier.label}
      </span>
    </>
  );
};

const CustomerTenureBadge = ({ profile, name }) => {
  const tier = profile?.tier || { key: "new_rider", label: "New Rider", blurb: "Welcome to the Spark garage." };
  const s = TENURE_STYLES[tier.key] || TENURE_STYLES.new_rider;
  const year = profile?.since_year || "—";
  const yearsWord = profile?.years_with_us >= 1
    ? `${profile.years_with_us} year${profile.years_with_us === 1 ? "" : "s"} with us`
    : "";
  return (
    <div className={`surface-card overflow-hidden p-4 sm:p-5 relative ${s.banner}`} data-testid="tracker-tenure-badge">
      <div className="absolute -right-6 -bottom-6 opacity-20 pointer-events-none">
        <Zap className="size-32" strokeWidth={1.5} />
      </div>
      <div className="relative flex items-start gap-3 flex-wrap">
        <div className="size-10 rounded-full bg-white/25 backdrop-blur flex items-center justify-center shrink-0">
          <Zap className="size-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-[0.22em] font-bold opacity-70">Spark Loyalty</p>
          <h3 className="font-display uppercase tracking-tight text-lg sm:text-xl font-bold mt-0.5">
            {name ? `Hey ${name.split(" ")[0]} — ` : ""}{tier.label}
          </h3>
          <p className="text-xs sm:text-sm opacity-90 mt-0.5">
            {tier.blurb} Rolling with us since <strong>{year}</strong>{yearsWord ? ` · ${yearsWord}` : ""}.
          </p>
        </div>
      </div>
    </div>
  );
};

const Tracker = () => {
  const [params, setParams] = useSearchParams();
  const [email, setEmail] = useState(params.get("email") || "");
  const [jobs, setJobs] = useState([]);
  const [orders, setOrders] = useState([]);
  const [customerProfile, setCustomerProfile] = useState(null);
  const [loyalty, setLoyalty] = useState(null);
  const [tab, setTab] = useState(params.get("tab") || "jobs"); // "jobs" | "orders"
  const [selectedRef, setSelectedRef] = useState(params.get("reference") || "");
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [approving, setApproving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedLineItem, setSelectedLineItem] = useState(null);

  const lookup = async (em, autoSelectRef) => {
    setLoading(true);
    setError(null);
    setJobs([]);
    setOrders([]);
    try {
      const res = await api.get("/track", { params: { email: em } });
      const list = res.data?.jobs || [];
      const orderList = res.data?.orders || [];
      setJobs(list);
      setOrders(orderList);
      setCustomerProfile(res.data?.customer_profile || null);
      setLoyalty(res.data?.loyalty || null);
      // Auto-pick the right tab if customer has only one type
      if (list.length === 0 && orderList.length > 0) setTab("orders");
      else if (list.length > 0 && orderList.length === 0) setTab("jobs");
      if (autoSelectRef && list.find((j) => j.ref_number === autoSelectRef)) {
        // Only deep-link to a specific job if explicitly requested via URL.
        setSelectedRef(autoSelectRef);
      } else {
        // Always show the unified dashboard with tabs — even for single results —
        // so customers can clearly see which side (jobs vs orders) they're viewing.
        setSelectedRef("");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Lookup failed");
    } finally {
      setLoading(false);
    }
  };

  const job = jobs.find((j) => j.ref_number === selectedRef) || null;

  const approveQuote = async () => {
    if (!job) return;
    setConfirmOpen(false);
    setApproving(true);
    try {
      await api.post("/jobs/approve-quote", { reference: job.ref_number, email });
      toast.success("Quote approved · we'll be in touch shortly");
      await lookup(email, job.ref_number);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Approval failed");
    } finally {
      setApproving(false);
    }
  };

  useEffect(() => {
    const em = params.get("email");
    if (em) lookup(em, params.get("reference") || "");
  }, []); // eslint-disable-line

  const handleSubmit = (e) => {
    e.preventDefault();
    const em = email.trim().toLowerCase();
    if (!em) {
      toast.error("Enter the email you used to book");
      return;
    }
    setEmail(em);
    setParams({ email: em });
    lookup(em);
  };

  const backToList = () => {
    setSelectedRef("");
    setParams({ email });
  };

  const submitCancel = async () => {
    if (!job) return;
    setCancelling(true);
    try {
      await api.post("/track/cancel", { email, reference: job.ref_number, reason: cancelReason });
      toast.success("Job cancelled. A confirmation email is on its way.");
      setCancelOpen(false);
      setCancelReason("");
      await lookup(email, selectedRef);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Cancel failed. Please try again.");
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="min-h-screen bg-spark-floor">
      <Navbar />

      <div className="bg-spark-teal-deep text-white border-b-4 border-spark-yellow" data-testid="tracker-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
          <p className="label-mono text-spark-yellow mb-2">Your account</p>
          <h1 className="font-display uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl font-bold leading-[0.95]">
            Repairs &amp; orders, one place
          </h1>
          <p className="mt-4 text-slate-300 max-w-xl">
            Enter the email you used and we'll pull up every repair booking and shop order on file. Track status, view tracking, send payment screenshots — all from one page.
          </p>
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
        <form onSubmit={handleSubmit} className="surface-card p-6 sm:p-8 mb-8 grid sm:grid-cols-3 gap-4 sm:items-end" data-testid="tracker-form">
          <div className="sm:col-span-2">
            <label className="label-mono mb-2 inline-block">Your email</label>
            <input
              type="email"
              className="input-style"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              data-testid="tracker-email-input"
              required
            />
          </div>
          <button type="submit" className="btn-primary w-full sm:w-auto" disabled={loading} data-testid="tracker-search-btn">
            {loading ? <Loader2 className="size-4 animate-spin" /> : <Search className="size-4" />} Track my scooter
          </button>
        </form>

        {error && (
          <div className="surface-card p-6 flex items-start gap-3 border-l-4 border-l-red-500" data-testid="tracker-error">
            <AlertCircle className="size-5 text-red-500 shrink-0 mt-0.5" />
            <div>
              <p className="font-display uppercase tracking-wider font-bold text-spark-ink">Couldn't find any bookings</p>
              <p className="text-sm text-spark-slate mt-1">{error}</p>
            </div>
          </div>
        )}

        {(jobs.length > 0 || orders.length > 0) && !job && (
          <div className="space-y-3" data-testid="tracker-tabs-wrap">
            {customerProfile && <CustomerTenureBadge profile={customerProfile} name={jobs[0]?.full_name || orders[0]?.customer_name} />}
            <LoyaltyCard loyalty={loyalty} name={jobs[0]?.full_name || orders[0]?.customer_name} />
            <div className="flex gap-1.5 border-b border-spark-line">
              <button
                onClick={() => { setTab("jobs"); setParams({ email, tab: "jobs" }); }}
                className={`px-4 py-2 text-sm font-display uppercase tracking-wider font-bold border-b-2 -mb-px transition ${tab === "jobs" ? "text-spark-ink border-spark-yellow" : "text-spark-slate border-transparent hover:text-spark-ink"}`}
                data-testid="tab-jobs"
              >
                Repair jobs <span className="ml-1.5 opacity-60">{jobs.length}</span>
              </button>
              <button
                onClick={() => { setTab("orders"); setParams({ email, tab: "orders" }); }}
                className={`px-4 py-2 text-sm font-display uppercase tracking-wider font-bold border-b-2 -mb-px transition ${tab === "orders" ? "text-spark-ink border-spark-yellow" : "text-spark-slate border-transparent hover:text-spark-ink"}`}
                data-testid="tab-orders"
              >
                Shop orders <span className="ml-1.5 opacity-60">{orders.length}</span>
              </button>
            </div>

            {tab === "jobs" && jobs.length === 0 && (
              <div className="surface-card p-8 text-center">
                <p className="text-spark-slate">No repair bookings on this email.</p>
              </div>
            )}
            {tab === "orders" && orders.length === 0 && (
              <div className="surface-card p-8 text-center">
                <p className="text-spark-slate">No shop orders on this email.</p>
              </div>
            )}

            {tab === "jobs" && jobs.length > 0 && (
              <div className="space-y-3" data-testid="tracker-job-list">
                <p className="label-mono text-spark-slate">{jobs.length} booking{jobs.length === 1 ? "" : "s"} on file</p>
                {jobs.map((j) => (
                  <button
                    key={j.ref_number}
                    onClick={() => {
                      setSelectedRef(j.ref_number);
                      setParams({ email, reference: j.ref_number });
                    }}
                    className="surface-card w-full p-5 sm:p-6 flex items-center justify-between gap-4 hover:border-spark-teal hover:shadow-md transition-all text-left"
                    data-testid={`tracker-job-card-${j.ref_number}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="font-display uppercase tracking-wider text-lg font-bold text-spark-ink">{j.ref_number}</span>
                        <span className={`px-2.5 py-0.5 rounded-sm font-display uppercase tracking-wider text-[10px] font-bold ${statusBadgeClasses(j.status)}`}>
                          {j.status}
                        </span>
                        {j.quote_locked && (
                          <span className="inline-flex items-center gap-1 text-xs text-spark-teal font-semibold">
                            <CheckCircle2 className="size-3.5" /> Quote approved
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-spark-slate mt-1.5 truncate">
                        {j.scooter_brand} {j.scooter_model} · <span className="capitalize">{j.service_type}</span> · booked {new Date(j.created_at).toLocaleDateString("en-NZ", { dateStyle: "medium" })}
                      </p>
                    </div>
                    <ChevronRight className="size-5 text-spark-slate shrink-0" />
                  </button>
                ))}
              </div>
            )}

            {tab === "orders" && orders.length > 0 && (
              <CustomerOrdersList orders={orders} />
            )}
          </div>
        )}

        {job && (
          <div className="space-y-6" data-testid="tracker-result">
            {jobs.length > 1 && (
              <button
                onClick={backToList}
                className="inline-flex items-center gap-1.5 text-sm text-spark-teal hover:text-spark-teal-deep font-semibold"
                data-testid="tracker-back-to-list"
              >
                <ArrowLeft className="size-4" /> All my bookings ({jobs.length})
              </button>
            )}
            <div className="surface-card p-6 sm:p-8 grid sm:grid-cols-2 gap-6">
              <div>
                <p className="label-mono mb-1">Reference</p>
                <p className="font-display uppercase tracking-wider text-2xl font-bold text-spark-ink">{job.ref_number}</p>
                <p className="text-sm text-spark-slate mt-2">{job.full_name}</p>
                {customerProfile && (
                  <div className="mt-3 flex flex-wrap items-center gap-1.5" data-testid="tracker-tenure-inline">
                    <TenurePill profile={customerProfile} />
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="label-mono mb-1">Scooter</p>
                  <p>{job.scooter_brand} {job.scooter_model}</p>
                </div>
                <div>
                  <p className="label-mono mb-1">Service</p>
                  <p className="capitalize">{job.service_type}</p>
                </div>
                <div>
                  <p className="label-mono mb-1">{job.pickup_required ? "Pickup time" : "Drop-off"}</p>
                  <p>{new Date(job.preferred_dropoff_at).toLocaleString("en-NZ", { dateStyle: "medium", timeStyle: "short" })}</p>
                </div>
                <div>
                  <p className="label-mono mb-1">Estimated</p>
                  <p>{job.estimated_price ? `$${Number(job.estimated_price).toFixed(2)}` : "—"}</p>
                </div>
              </div>
              {(job.pickup_required || job.dropoff_required) && (
                <div className="mt-4 pt-4 border-t border-spark-line" data-testid="tracker-logistics">
                  <p className="label-mono mb-1.5">Pickup / Return</p>
                  <div className="flex flex-wrap items-center gap-2 mb-1.5">
                    {job.pickup_required && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-spark-teal-light text-spark-teal-dark text-xs font-display uppercase tracking-wider font-bold">Pickup · $40</span>}
                    {job.dropoff_required && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-spark-teal-light text-spark-teal-dark text-xs font-display uppercase tracking-wider font-bold">Return · $40</span>}
                  </div>
                  {job.dropoff_required && (
                    <p className="text-[11px] text-spark-slate italic mb-1.5">Return time will be confirmed once your scooter is ready.</p>
                  )}
                  {job.service_address && <p className="text-sm whitespace-pre-wrap text-spark-slate">{job.service_address}</p>}
                </div>
              )}
            </div>

            <StatusStepper currentStatus={job.status} history={job.status_history} />

            {job.status !== "Cancelled" && job.status !== "Completed" && job.status !== "Ready for Pickup" && (
              <div className="text-right">
                <button
                  type="button"
                  onClick={() => setCancelOpen(true)}
                  className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider font-semibold text-spark-slate hover:text-red-600 underline underline-offset-4"
                  data-testid="tracker-cancel-btn"
                >
                  <X className="size-3.5" /> Cancel this job
                </button>
              </div>
            )}
            {job.status === "Cancelled" && (
              <div className="rounded-md border border-slate-300 bg-slate-100 text-slate-700 px-4 py-3 text-sm" data-testid="tracker-cancelled-notice">
                <p className="font-display uppercase tracking-wider text-xs font-bold">This job has been cancelled</p>
                <p className="text-xs mt-0.5 text-slate-500">No further work will be carried out. If this was a mistake, reply to your cancellation email or book a new job.</p>
              </div>
            )}

            {job.checklist_summary && <ChecklistSummaryCard job={job} customerEmail={email} />}

            <LoyaltyCard loyalty={loyalty} name={job.full_name} />

            {!job.quote_items?.length && job.status === "Awaiting Quote Approval" && (
              <div className="surface-card p-5 sm:p-6" data-testid="tracker-quote-pending">
                <p className="label-mono text-spark-teal mb-1">Estimated quote</p>
                <p className="text-sm text-spark-slate">Your quote is being finalised. You'll be able to review and approve it here shortly.</p>
              </div>
            )}

            {job.quote_items?.length > 0 && (
              <div className="surface-card p-5 sm:p-8" data-testid="tracker-quote">
                <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
                  <p className="label-mono text-spark-teal">Estimated quote</p>
                  {job.quote_locked && (
                    <span className="inline-flex items-center gap-1.5 bg-spark-yellow text-spark-ink px-2.5 py-1 rounded-sm font-display uppercase tracking-wider text-xs font-bold" data-testid="tracker-approved-badge">
                      <CheckCircle2 className="size-3.5" /> Approved · {new Date(job.quote_approved_at).toLocaleDateString("en-NZ", { dateStyle: "medium" })}
                    </span>
                  )}
                </div>
                <p className="text-xs text-spark-slate mb-3">Tap any line for more details on what's included.</p>
                <ul className="divide-y divide-spark-line border border-spark-line rounded-md bg-white">
                  {job.quote_items.map((q, i) => (
                    <li key={i}>
                      <button
                        type="button"
                        onClick={() => q.kind !== "discount" && setSelectedLineItem(q)}
                        className="w-full px-4 py-3 flex items-center justify-between gap-3 text-sm text-left hover:bg-spark-floor transition group"
                        data-testid={`quote-line-${i}`}
                      >
                        <span className="text-spark-ink truncate flex-1 min-w-0 group-hover:text-spark-teal-dark">{q.name}</span>
                        <span className={`font-display uppercase tracking-tight font-bold shrink-0 ${q.price < 0 ? "text-emerald-700" : "text-spark-ink"}`}>{q.price < 0 ? "-" : ""}${Math.abs(Number(q.price || 0)).toFixed(2)}{q.qty > 1 ? ` × ${q.qty}` : ""}</span>
                        {q.kind !== "discount" && <ChevronRight className="size-4 text-spark-slate group-hover:text-spark-teal shrink-0" />}
                      </button>
                    </li>
                  ))}
                  <li className="bg-spark-teal text-white px-4 py-3 flex items-center justify-between rounded-b-md">
                    <span className="label-mono text-spark-yellow">Estimated total</span>
                    <span className="font-display uppercase tracking-tight text-2xl font-bold" data-testid="tracker-quote-total">
                      ${Number(job.quoted_total || 0).toFixed(2)}
                    </span>
                  </li>
                </ul>

                {!job.quote_locked ? (
                  <div className="mt-5 bg-spark-teal-deep text-white rounded-md p-5 grid sm:grid-cols-12 gap-4 items-center" data-testid="tracker-approve-cta">
                    <div className="sm:col-span-8">
                      <p className="label-mono text-spark-yellow mb-1">Happy with this quote?</p>
                      <p className="font-display uppercase tracking-tight text-xl font-bold leading-tight">Approve to lock it in.</p>
                      <p className="text-xs text-slate-300 mt-1">We won't start work until you approve. If we discover anything new, we'll always pause and re-quote.</p>
                    </div>
                    <div className="sm:col-span-4 sm:text-right">
                      <button onClick={() => setConfirmOpen(true)} disabled={approving} className="btn-accent w-full sm:w-auto" data-testid="customer-approve-btn">
                        {approving ? <Loader2 className="size-4 animate-spin" /> : <ShieldCheck className="size-4" />} Approve Quote
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-spark-slate italic mt-3">
                    This quote was approved by the {job.quote_approved_by_label === "customer" ? "customer" : "workshop"} on {new Date(job.quote_approved_at).toLocaleString("en-NZ", { dateStyle: "medium", timeStyle: "short" })}.
                  </p>
                )}
                {!job.quote_locked && (
                  <p className="text-xs text-spark-slate italic mt-3">Final price may differ after diagnosis. Special-order parts (batteries, motors, controllers) may require a deposit of 50% of the total quote before ordering, with the balance due on completion.</p>
                )}
              </div>
            )}

            {job.timeline_updates?.length > 0 && (
              <div className="surface-card p-5 sm:p-8" data-testid="tracker-timeline">
                <div className="flex items-center gap-2 mb-4">
                  <MessageCircle className="size-5 text-spark-teal" />
                  <p className="label-mono text-spark-teal">Updates from the workshop</p>
                </div>
                <ol className="space-y-4">
                  {[...job.timeline_updates].reverse().map((u) => (
                    <li key={u.id} className="border-l-4 border-spark-yellow pl-4 py-1">
                      {u.message && <p className="text-sm sm:text-base text-spark-ink leading-relaxed whitespace-pre-wrap">{u.message}</p>}
                      {u.media?.length > 0 && (
                        <ul className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2" data-testid={`timeline-media-${u.id}`}>
                          {u.media.map((m) => {
                            const url = fileUrl(m.storage_path);
                            const isVideo = m.content_type?.startsWith("video/");
                            return (
                              <li key={m.id} className="aspect-square overflow-hidden rounded-md border border-spark-line bg-spark-floor">
                                <a href={url} target="_blank" rel="noreferrer" className="block w-full h-full">
                                  {isVideo ? (
                                    <video src={url} className="w-full h-full object-cover" controls />
                                  ) : (
                                    <img src={url} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" />
                                  )}
                                </a>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                      <p className="text-xs text-spark-slate mt-2">
                        {new Date(u.posted_at).toLocaleString("en-NZ", { dateStyle: "medium", timeStyle: "short" })}
                      </p>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            <TrackerDepositCard job={job} />

            {(job.final_price != null || job.quote_approved_at) && (
              <PaymentStatusBlock job={job} customerEmail={email} onUploaded={() => lookup(email, selectedRef)} />
            )}
          </div>
        )}
      </main>
      {confirmOpen && job && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center px-4 py-8 bg-spark-teal-deep/85 backdrop-blur-sm animate-in fade-in duration-200"
          onClick={() => !approving && setConfirmOpen(false)}
          data-testid="approve-confirm-modal"
        >
          <div
            className="relative w-full max-w-lg bg-white rounded-md overflow-hidden shadow-2xl animate-in zoom-in-95 slide-in-from-bottom-4 duration-300"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-spark-teal-deep text-white px-6 py-5 flex items-start justify-between gap-3 border-b-4 border-spark-yellow">
              <div className="flex items-start gap-3">
                <div className="bg-spark-yellow text-spark-ink size-10 rounded-md flex items-center justify-center shrink-0">
                  <ShieldCheck className="size-5" />
                </div>
                <div>
                  <p className="label-mono text-spark-yellow mb-1">Final check</p>
                  <h2 className="font-display uppercase tracking-tight text-2xl font-bold leading-tight">Approve this quote?</h2>
                </div>
              </div>
              <button
                onClick={() => setConfirmOpen(false)}
                disabled={approving}
                className="text-slate-300 hover:text-white transition-colors p-1 -m-1"
                aria-label="Close"
                data-testid="approve-confirm-close"
              >
                <X className="size-5" />
              </button>
            </div>
            <div className="px-6 py-6 space-y-5">
              <p className="text-sm text-spark-ink leading-relaxed">
                You're about to lock in <span className="font-display uppercase tracking-wider font-bold">{job.ref_number}</span> for
                <span className="font-semibold"> {job.scooter_brand} {job.scooter_model}</span>. By approving you confirm the listed services and prices. We'll start work as agreed and pause to re-quote if we discover anything new.
              </p>
              <div className="bg-spark-floor border border-spark-line rounded-md p-4 flex items-center justify-between">
                <span className="label-mono text-spark-slate">Estimated total</span>
                <span className="font-display uppercase tracking-tight text-3xl font-bold text-spark-ink" data-testid="approve-confirm-total">
                  ${Number(job.quoted_total || 0).toFixed(2)}
                </span>
              </div>
              <p className="text-xs text-spark-slate italic">
                Special-order parts (batteries, motors, controllers) may require a deposit of 50% of the total quote before ordering, with the balance due on completion.
                All labour is covered by our 21-day workshop warranty.
              </p>
              <p className="text-xs text-spark-ink" data-testid="approve-terms-note">
                By approving you agree to our{" "}
                <a href="/terms" target="_blank" rel="noreferrer" className="text-spark-teal-dark underline font-semibold" data-testid="approve-terms-link">Terms & Conditions</a>.
              </p>
            </div>
            <div className="px-6 py-4 bg-spark-floor border-t border-spark-line flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <button
                onClick={() => setConfirmOpen(false)}
                disabled={approving}
                className="inline-flex items-center justify-center gap-2 border border-spark-line text-spark-slate hover:text-spark-ink hover:bg-white px-5 py-3 font-semibold uppercase tracking-wider text-sm rounded-md transition-colors disabled:opacity-50 disabled:pointer-events-none"
                data-testid="approve-confirm-cancel"
              >
                Not yet
              </button>
              <button
                onClick={approveQuote}
                disabled={approving}
                className="btn-accent"
                data-testid="approve-confirm-confirm"
              >
                {approving ? <Loader2 className="size-4 animate-spin" /> : <ShieldCheck className="size-4" />}
                Yes, approve quote
              </button>
            </div>
          </div>
        </div>
      )}
      {cancelOpen && job && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="tracker-cancel-modal" role="dialog" aria-modal="true">
          <div className="w-full max-w-md rounded-xl border border-spark-line bg-white shadow-2xl overflow-hidden">
            <div className="bg-slate-100 border-b border-spark-line px-6 py-4 flex items-center gap-3">
              <X className="size-5 text-slate-600" />
              <h2 className="font-display uppercase tracking-wider text-base font-bold text-spark-ink">Cancel this job?</h2>
            </div>
            <div className="px-6 py-5 text-sm text-spark-ink space-y-3">
              <p>You&apos;re about to cancel <strong>{job.ref_number}</strong> — {job.scooter_brand} {job.scooter_model}.</p>
              <p className="text-spark-slate">We&apos;ll send a cancellation email to <strong className="text-spark-ink">{email}</strong> confirming no further work will be carried out. The job stays in your history for reference.</p>
              <div>
                <label className="label-mono mb-1.5 inline-block">Reason (optional)</label>
                <textarea
                  rows={3}
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="Anything you'd like us to know? (Not required.)"
                  className="input-style resize-none"
                  data-testid="tracker-cancel-reason"
                />
              </div>
            </div>
            <div className="px-6 py-4 bg-spark-floor border-t border-spark-line flex items-center justify-end gap-2">
              <button onClick={() => { setCancelOpen(false); setCancelReason(""); }} disabled={cancelling} className="px-4 py-2 rounded-md text-xs uppercase tracking-wider font-semibold text-spark-slate hover:bg-white" data-testid="tracker-cancel-back">
                Never mind
              </button>
              <button onClick={submitCancel} disabled={cancelling} className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-slate-600 text-white text-xs uppercase tracking-wider font-bold hover:bg-slate-700 disabled:opacity-60" data-testid="tracker-cancel-confirm">
                {cancelling ? <Loader2 className="size-3.5 animate-spin" /> : <X className="size-3.5" />}
                {cancelling ? "Cancelling…" : "Yes, cancel job"}
              </button>
            </div>
          </div>
        </div>
      )}
      <Footer />
      <LineItemDetailDialog open={!!selectedLineItem} item={selectedLineItem} onClose={() => setSelectedLineItem(null)} />
    </div>
  );
};

export default Tracker;
